import {
  Component, ComponentFactoryResolver, ComponentRef, ElementRef,
  OnDestroy, OnInit,
  ViewChild, ViewContainerRef
} from '@angular/core';
import { InteractionService } from '../../../services/interaction.service';
import { SynchronizationService } from '../../../services/synchronization.service';
import { GalleryViewOrderedDirective } from './gallery-ordered-view/gallery-view-ordered.directive';
import { GalleryViewOrderedComponent } from './gallery-ordered-view/gallery-view-ordered.component';
import { GalleryViewSmallDirective } from './gallery-small-view/gallery-view-small.directive';
import { GalleryViewSmallComponent } from './gallery-small-view/gallery-view-small.component';
import { Subscription } from 'rxjs';
import { SettingsService } from '../../../services/settings.service';
import { HTMLElementPositioningHelper } from '../../../util/htmlelement-positioning-helper';
import { WidgetWrapperComponent } from '../../widget-wrapper/widget-wrapper.component';
import { IWidget } from '../../../interfaces/iWidget';

@Component({
  selector: 'app-gallery-widget',
  templateUrl: './gallery-widget.component.html',
  styleUrls: ['./gallery-widget.component.scss'],
  })

export class GalleryWidgetComponent implements IWidget, OnInit, OnDestroy {
  static widgetName = 'gallery';
  @ViewChild('divcontainer', { static: true }) private element: ElementRef;
  private interactedClass = 'resizedwidget';
  private viewContainerRef: ViewContainerRef;
  private event: Subscription[] = [];
  public parent: WidgetWrapperComponent;

  // style
  public zIndex = 25;

  // ref
  public componentRef: ComponentRef<any>;
  private _isUndocked: boolean;

  set isUndocked(isUndocked: boolean) {
    this._isUndocked = isUndocked;

    if (isUndocked) {
      this.loadOrderedGalleryView();
    } else {
      this.loadSmallGalleryView();
    }
  }

  // Gallery View Directives
  @ViewChild(GalleryViewOrderedDirective, { static: true }) galleryViewOrdered: GalleryViewOrderedDirective;
  @ViewChild(GalleryViewSmallDirective, { static: true }) galleryViewSmall: GalleryViewSmallDirective;

  constructor(
    private interactionService: InteractionService,
    private synchronization: SynchronizationService,
    private componentFactoryResolver: ComponentFactoryResolver,
    private settingsService: SettingsService,
    private positionHelper: HTMLElementPositioningHelper) {
  }

  ngOnInit() {
    this.event.push(this.settingsService.getUpdateObservable().subscribe(res => this.updateOnProjectLoad()));
    this.loadSmallGalleryView();
    this.setupRealTimeEvents();
  }

  ngOnDestroy(): void {
    for (const ev of this.event) {
      ev.unsubscribe();
    }
  }

  /**
   * Set up the event listeners for real-time synchronization.
   */
  private setupRealTimeEvents() {
    const obj = this;
    this.event.push(this.synchronization.subscribeEvent<any>(GalleryWidgetComponent.widgetName + 'ResizeWidget')
      .subscribe(size => {
        const target = obj.element.nativeElement;
        target.style.width = `${size.width}px`;
        target.style.height = `${size.height}px`;
      }));
    this.event.push(this.synchronization.subscribeEvent<any>(GalleryWidgetComponent.widgetName + 'StartMoveWidget')
      .subscribe(() => {
        const target = this.element.nativeElement;
        target.classList.add(this.interactedClass);
      }));
    this.event.push(this.synchronization.subscribeEvent<any>(GalleryWidgetComponent.widgetName + 'MoveWidget')
      .subscribe(positionRelative => {
        const target = this.element.nativeElement;
        const scale = (parseFloat(target.getAttribute('data-scale')) || 1);
        this.positionHelper.setRelativePosition(target, positionRelative.left, positionRelative.top, null, scale);
      }));
    this.event.push(this.synchronization.subscribeEvent<any>(GalleryWidgetComponent.widgetName + 'SwitchToOrderedView')
      .subscribe(size => {
        obj.loadOrderedGalleryView();
        const target = obj.element.nativeElement;
        target.style.width = size.width;
        target.style.height = size.height;
      }));
  }

  /**
   * Loads the small gallery view and clears the previous view inside the viewContainer.
   * **/
  loadSmallGalleryView() {
    // Cleanup previous ViewContainer
    if (this.viewContainerRef !== undefined) {
      this.viewContainerRef.clear();
    }
    this.viewContainerRef = this.galleryViewSmall.viewContainerRef;
    const componentRef = this.viewContainerRef.createComponent(GalleryViewSmallComponent);
  }

  /**
   * Loads the ordered gallery view and clears the previous view inside the viewContainer.
   * **/
  loadOrderedGalleryView() {
    // Cleanup previous ViewContainer
    if (this.viewContainerRef !== undefined) {
      this.viewContainerRef.clear();
    }
    this.viewContainerRef = this.galleryViewOrdered.viewContainerRef;
    const componentRef = this.viewContainerRef.createComponent(GalleryViewOrderedComponent);

    // Use Component
    (<GalleryViewOrderedComponent>componentRef.instance).data = { viewName: GalleryWidgetComponent.widgetName };
  }

  /**
   * Reloads the gallery when a new project is loaded.
   */
  updateOnProjectLoad(): void {
    this.loadSmallGalleryView();
    this.removeAllImagesFromView();
  }

  /**
   * Removes all images with the passed from the view.
   */
  removeAllImagesFromView() {
    const imgs = document.querySelectorAll('[data-imageid]');
    for (let i = 0; i < imgs.length; i++) {
      imgs[i].parentElement.removeChild(imgs[i]);
    }
  }

  setPosition(x: number, y: number) {
    this.element.nativeElement.style.left = x + 'px';
    this.element.nativeElement.style.top = y + 'px';
  }
}
